import {
  validateFieldValue,
  type FieldDefinition,
  type TypedFieldValue,
} from '@/components/objects/typedFieldData';

export type WidgetSpan = 'single' | 'wide';
export interface WidgetPlacement {
  widgetId: string;
  span: WidgetSpan;
}
export function layoutError(layout: readonly WidgetPlacement[], widgetIds: readonly string[]): string | null {
  if (new Set(widgetIds).size !== widgetIds.length) return 'Идентификаторы виджетов повторяются.';
  if (new Set(layout.map((item) => item.widgetId)).size !== layout.length)
    return 'Виджет уже добавлен в обзор.';
  if (layout.some((item) => !widgetIds.includes(item.widgetId) || !['single', 'wide'].includes(item.span)))
    return 'Настройки обзора не соответствуют доступным виджетам.';
  return null;
}
export function moveWidget(
  layout: readonly WidgetPlacement[],
  id: string,
  direction: -1 | 1,
): WidgetPlacement[] {
  const next = layout.map((item) => ({ ...item })),
    index = next.findIndex((item) => item.widgetId === id),
    to = index + direction;
  if (index >= 0 && to >= 0 && to < next.length) [next[index], next[to]] = [next[to], next[index]];
  return next;
}
export type CsvDelimiter = ',' | ';' | '\t';
export type CsvResult =
  { headers: string[]; rows: string[][]; error: null } | { headers: []; rows: []; error: string };
const failCsv = (error: string): CsvResult => ({ headers: [], rows: [], error });
/** Strict RFC4180-style quoting, deterministic delimiter. Never evaluates cell text. */
export function parseCsv(input: string, delimiter: CsvDelimiter = ','): CsvResult {
  if (![',', ';', '\t'].includes(delimiter)) return failCsv('Неизвестный разделитель.');
  if (input.length > 2_000_000 || new TextEncoder().encode(input).byteLength > 2_000_000)
    return failCsv('Для одного импорта доступно до 2 МБ текста.');
  const text = input.replace(/^\uFEFF/u, '');
  if (!text.trim()) return failCsv('Файл пуст.');
  const records: string[][] = [],
    row: string[] = [];
  let cell = '',
    quoted = false,
    closed = false,
    rowNumber = 1;
  const pushCell = () => {
    row.push(cell);
    cell = '';
    closed = false;
  };
  const pushRow = (): string | null => {
    pushCell();
    records.push([...row]);
    row.length = 0;
    rowNumber++;
    if (records.length > 5001) return 'Для одного импорта доступно до 5000 строк.';
    return null;
  };
  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    if (quoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          cell += '"';
          index++;
        } else {
          quoted = false;
          closed = true;
        }
      } else cell += char;
    } else if (char === delimiter) {
      pushCell();
      if (row.length > 99) return failCsv('Для одного импорта доступно до 100 колонок.');
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && text[index + 1] === '\n') index++;
      const error = pushRow();
      if (error) return failCsv(error);
    } else if (closed) {
      return failCsv(
        'После закрывающей кавычки нужен разделитель или конец строки. Запись ' + rowNumber + '.',
      );
    } else if (char === '"') {
      if (cell.length) return failCsv('Кавычка внутри неэкранированного значения. Запись ' + rowNumber + '.');
      quoted = true;
    } else cell += char;
  }
  if (quoted) return failCsv('Не закрыта кавычка. Запись ' + rowNumber + '.');
  if (cell.length || row.length || closed) {
    const error = pushRow();
    if (error) return failCsv(error);
  }
  const [headers, ...rows] = records;
  if (!headers || headers.every((header) => !header.trim()))
    return failCsv('В первой строке нужны названия колонок.');
  const bad = rows.findIndex((item) => item.length !== headers.length);
  if (bad >= 0) return failCsv('Число значений не совпадает с заголовком. Запись ' + (bad + 2) + '.');
  return { headers, rows, error: null };
}
export interface ImportColumn {
  definition: FieldDefinition;
  parse: (raw: string) => TypedFieldValue;
}
export type ColumnMapping = Readonly<Record<string, number | null>>;
export interface ImportIssue {
  row: number | null;
  fieldId: string;
  message: string;
}
export interface ValidatedImport {
  records: Readonly<Record<string, TypedFieldValue>>[];
  issues: ImportIssue[];
}
export function validateImport(
  csv: CsvResult,
  columns: readonly ImportColumn[],
  mapping: ColumnMapping,
): ValidatedImport {
  if (csv.error) return { records: [], issues: [{ row: null, fieldId: '', message: csv.error }] };
  const issues: ImportIssue[] = [],
    records: Record<string, TypedFieldValue>[] = [];
  const ids = columns.map((column) => column.definition.id);
  if (new Set(ids).size !== ids.length)
    return {
      records: [],
      issues: [{ row: null, fieldId: '', message: 'Идентификаторы полей повторяются.' }],
    };
  for (const { definition } of columns) {
    const source = mapping[definition.id];
    if (source == null) {
      if (definition.required)
        issues.push({
          row: null,
          fieldId: definition.id,
          message: 'Сопоставь обязательное поле «' + definition.label + '».',
        });
    } else if (!Number.isInteger(source) || source < 0 || source >= csv.headers.length) {
      issues.push({ row: null, fieldId: definition.id, message: 'Колонка источника недоступна.' });
    }
  }
  if (!columns.some((column) => mapping[column.definition.id] != null))
    issues.push({ row: null, fieldId: '', message: 'Сопоставь хотя бы одно поле.' });
  if (issues.length) return { records: [], issues };
  csv.rows.forEach((row, index) => {
    const record: Record<string, TypedFieldValue> = Object.create(null);
    for (const column of columns) {
      const source = mapping[column.definition.id];
      if (source == null) continue;
      try {
        const value = column.parse(row[source]);
        const error = validateFieldValue(column.definition, value);
        if (error) issues.push({ row: index + 2, fieldId: column.definition.id, message: error });
        else record[column.definition.id] = value;
      } catch (error) {
        issues.push({
          row: index + 2,
          fieldId: column.definition.id,
          message: error instanceof Error ? error.message : 'Значение не удалось прочитать.',
        });
      }
    }
    records.push(record);
  });
  // No partially valid payload: caller either receives the complete batch or zero records.
  return { records: issues.length ? [] : records, issues };
}
export type CsvCell = string | number | boolean | null | undefined;
/** Every text cell is formula-neutralized before standard CSV escaping. */
export function exportCsv(
  headers: readonly string[],
  rows: readonly (readonly CsvCell[])[],
  delimiter: CsvDelimiter = ',',
): string {
  if (![',', ';', '\t'].includes(delimiter)) throw new Error('Неизвестный разделитель.');
  if (rows.some((row) => row.length !== headers.length))
    throw new Error('Число ячеек не совпадает с заголовком.');
  const encode = (value: CsvCell): string => {
    let text = value == null ? '' : String(value);
    if (typeof value === 'number' && !Number.isFinite(value))
      throw new Error('Нельзя выгрузить нечисловое значение.');
    // Strings may be interpreted as formulas even after whitespace/control characters.
    if (typeof value === 'string') {
      let index = 0;
      while (index < text.length && (text[index].trim() === '' || text.charCodeAt(index) < 32)) index++;
      if (['=', '+', '-', '@'].includes(text[index]) || ['\t', '\r', '\n'].includes(text[0]))
        text = "'" + text;
    }
    return /["\r\n]/u.test(text) || text.includes(delimiter) ? '"' + text.replace(/"/gu, '""') + '"' : text;
  };
  return '\uFEFF' + [headers, ...rows].map((row) => row.map(encode).join(delimiter)).join('\r\n');
}
export type ObjectVersion = Readonly<Record<string, TypedFieldValue>>;
export type ChangeKind = 'added' | 'removed' | 'changed' | 'same';
export interface FieldChange {
  definition: FieldDefinition;
  kind: ChangeKind;
  before?: TypedFieldValue;
  after?: TypedFieldValue;
}
function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  if (typeof value === 'object' && value !== null)
    return (
      '{' +
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => JSON.stringify(key) + ':' + canonical(item))
        .join(',') +
      '}'
    );
  return JSON.stringify(value);
}
export function compareVersions(
  definitions: readonly FieldDefinition[],
  before: ObjectVersion,
  after: ObjectVersion,
): FieldChange[] {
  return definitions.map((definition) => {
    const hasBefore = Object.hasOwn(before, definition.id),
      hasAfter = Object.hasOwn(after, definition.id);
    const previous = hasBefore ? before[definition.id] : undefined,
      next = hasAfter ? after[definition.id] : undefined;
    const kind: ChangeKind =
      !hasBefore && hasAfter
        ? 'added'
        : hasBefore && !hasAfter
          ? 'removed'
          : canonical(previous) === canonical(next)
            ? 'same'
            : 'changed';
    return { definition, before: previous, after: next, kind };
  });
}
